import { gql, useMutation } from '@apollo/client';
import { useRef, useState } from "react";
import { Column, createRecord, insertRecordBySeq, ResultTable, ScanRecord } from "./components/ResultTable";
import { Pda } from "./components/Pda";
import { parseException, showError } from "utils/exceptionUtils";
import { ScanInput } from "./components/ScanInput";
import { Header } from './components/Header';
import { isBagNumber, isTtBagNumber, showBarCodeError } from '../../utils/BarCodeUtil';
import { playScanFailedAoSound } from "../../utils/scanUtils";
import { t } from '../../i18n';
import { useScanQueueConsumer } from "./components/useScanQueueConsumer";
import { Dialog } from 'antd-mobile';


export interface HandleScanEventResult {
  success: boolean;
}

const HANDLE_WHS_UNBAGGING_SCAN_EVENT = gql`
mutation HandleWhsUnbaggingScanEvent($bagNumber: String!) {
    success: handleWhsUnbaggingScanEvent(bagNumber: $bagNumber)
}
`

const PageBody = () => {

  const [handleWhsUnbaggingScanEvent] = useMutation<HandleScanEventResult>(HANDLE_WHS_UNBAGGING_SCAN_EVENT);

  const handleScanEvent = async (bagNumber: string): Promise<HandleScanEventResult> => {
    const { data } = await handleWhsUnbaggingScanEvent({ variables: { bagNumber } });
    return { success: data?.success || false };
  };

  const [successRecords, setSuccessRecords] = useState<ScanRecord[]>([]);
  const [failedRecords, setFailedRecords] = useState<ScanRecord[]>([]);
  const [bagNumber, setBagNumber] = useState('');
  const currentDialogSettleRef = useRef<((result: boolean) => void) | null>(null);

  const onProcess = async (event: { id: string; seq: number; code: string; ts: number }) => {
    const barCode = event.code;
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    const value = barCode.trim();
    if (value === '') {
      playScanFailedAoSound();
      showError({ title: t('pda.common.error'), message: t('pda.common.bagCodeRequired') });
      return;
    }

    setBagNumber(value);

    const tailDigits = value.length > 6 ? value.slice(-6) : value;
    const confirmed = await new Promise<boolean>((resolve) => {
      let settled = false;
      const settle = (result: boolean) => {
        if (settled) return;
        settled = true;
        currentDialogSettleRef.current = null;
        handler.close();
        resolve(result);
      };
      currentDialogSettleRef.current = settle;
      const handler = Dialog.show({
        bodyStyle: { backgroundColor: '#111', borderRadius: '12px', padding: 0, overflow: 'hidden' },
        closeOnMaskClick: true,
        onClose: () => settle(false),
        content: (
          <div style={{ color: '#fff', padding: '16px' }}>
            <div style={{ position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '16px' }}>
              <span style={{ fontSize: '17px', fontWeight: 'bold' }}>
                ⚠️ {t('pda.pages.unbagging.confirmDialog.title')}
              </span>
              <button
                onClick={() => settle(false)}
                style={{ position: 'absolute', right: 0, background: 'none', border: 'none', color: '#fff', fontSize: '22px', cursor: 'pointer', padding: 0, lineHeight: 1 }}
              >
                ✕
              </button>
            </div>
            <div style={{ textAlign: 'center', paddingBottom: '16px' }}>
              <p style={{ fontSize: '13px', color: '#aaa', marginBottom: '6px' }}>
                {t('pda.pages.unbagging.confirmDialog.bagSuffix')}
              </p>
              <p style={{ fontSize: '40px', fontWeight: 'bold', marginBottom: '12px' }}>
                {tailDigits}
              </p>
              <p style={{ fontSize: '20px', fontWeight: 'bold' }}>
                {t('pda.pages.unbagging.confirmDialog.willUnbag')}
              </p>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => settle(false)}
                style={{ flex: 1, padding: '12px', background: '#333', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '16px', cursor: 'pointer' }}
              >
                {t('pda.pages.unbagging.confirmDialog.cancel')}
              </button>
              <button
                onClick={() => settle(true)}
                style={{ flex: 1, padding: '12px', background: '#333', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer' }}
              >
                {t('pda.pages.unbagging.confirmDialog.confirm')}
              </button>
            </div>
          </div>
        ),
      });
    });

    if (!confirmed) {
      setBagNumber('');
      return;
    }

    try {
      const { success } = await handleScanEvent(value);
      if (success) {
        setSuccessRecords(prev => insertRecordBySeq(prev, createRecord({ value1: value, seq: event.seq })));
        setBagNumber('');
      }
    } catch (error) {
      const { title, message: errorMessage } = parseException(error);
      playScanFailedAoSound();
      showError({ title, message: errorMessage });
      setFailedRecords(prev => insertRecordBySeq(prev, createRecord({ value1: value, failedReason: errorMessage, seq: event.seq })));
      setBagNumber('');
    }
  };

  const { enqueueScan } = useScanQueueConsumer({ maxConcurrency: 1, onProcess });

  const handleScan = (code: string) => {
    if (isBagNumber(code) || isTtBagNumber(code)) {
      currentDialogSettleRef.current?.(false);
      enqueueScan(code);
    } else {
      showBarCodeError(code);
    }
  };

  const successColumns: Column[] = [
    { title: t('pda.pages.unbagging.bagCode'), key: 'value1' },
    { title: t('pda.common.time'), key: 'time' }
  ];

  const failedColumns = [
    ...successColumns,
    { title: t('pda.common.failedReason'), key: 'failedReason' }
  ];

  return (
    <div className="flex flex-col gap-2">
      <ScanInput placeholder={t('pda.pages.unbagging.bagCode')} value={bagNumber} onScan={handleScan} />

      <ResultTable
        successRecords={successRecords}
        failedRecords={failedRecords}
        successColumns={successColumns}
        failedColumns={failedColumns}
      />
    </div>
  );

}

export default function UnbaggingPage() {
  return (
    <div className="w-full h-full flex flex-col gap-2 p-2">
      <Header title={t('pda.pages.unbagging.unbaggingScan')} />
      <Pda />
      <PageBody />
    </div>
  );
}
